import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import PhoneInput from '../../../../../components/form/PhoneInput';
import { resolveApiErrorMessage } from '../../../../../utils/apiErrorMessage';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type EmployeeRow = {
  id: string;
  email: string;
  type: string;
  recordKind?: 'SYSTEM_USER' | 'PERSONNEL_SIMPLE';
  employeeCode?: string | null;
  status: string;
  isEmailVerified?: boolean;
  isPrimaryAdmin?: boolean;
  createdAt: string;
  blockedReason?: string | null;
  blockedAt?: string | null;
  roles?: Array<{ id: string; name: string }>;
  departments?: Array<{ id: string; name: string; slug: string; isActive?: boolean }>;
  employeeProfile?: {
    fullName?: string | null;
    roleTitle?: string | null;
    phone?: string | null;
  } | null;
};

type RoleRow = {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
};

type DepartmentRow = {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
};

function fallbackRoleLabel(type: string): string {
  const normalized = String(type || '').toUpperCase();
  if (normalized === 'ADMIN') return 'Admin';
  if (normalized === 'SHIPPING_AGENT') return 'Shipping agent';
  if (normalized === 'EMPLOYEE') return 'Employee';
  return normalized || '—';
}

function EmployeesPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState<string>('');
  const [inviteCreateUserAccount, setInviteCreateUserAccount] = useState(true);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [, setRolesLoading] = useState(false);
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<Record<string, boolean>>({});
  const [inviteDepartmentRoleIds, setInviteDepartmentRoleIds] = useState<Record<string, string>>({});
  const [inviteDepartmentBaseSalaries, setInviteDepartmentBaseSalaries] = useState<Record<string, string>>({});

  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees`);
        if (!res.ok) {
          throw new Error(
            await resolveApiErrorMessage(res, t('backoffice.employees_error', 'Unable to load employees.')),
          );
        }
        const data = await res.json();
        setEmployees(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('backoffice.employees_error', 'Unable to load employees.'),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [authorizedFetch, t]);

  useEffect(() => {
    (async () => {
      setRolesLoading(true);
      try {
        const res = await authorizedFetch(`${API_BASE}/rbac/roles`);
        if (!res.ok) return;
        const data = (await res.json()) as RoleRow[];
        setRoles(data);
      } catch {
        // ignore; role UI will show load error in modal when needed
      } finally {
        setRolesLoading(false);
      }
    })();
  }, [authorizedFetch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok) return;
        const data = (await res.json()) as DepartmentRow[];
        setDepartments((Array.isArray(data) ? data : []).filter((d) => d.isActive));
      } catch {
        // ignore; invite submit will still validate server-side
      }
    })();
  }, [authorizedFetch]);

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteSubmitting(true);
    setInviteSuccess(null);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/workspace/employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: inviteEmail || undefined,
          createUserAccount: inviteCreateUserAccount,
          fullName: inviteName || undefined,
          phone: invitePhone || undefined,
          roleIds: inviteCreateUserAccount && inviteRoleId ? [inviteRoleId] : [],
          departmentIds: Object.entries(selectedDepartmentIds)
            .filter(([, checked]) => checked)
            .map(([departmentId]) => departmentId),
          departmentRoleAssignments: inviteCreateUserAccount
            ? Object.entries(selectedDepartmentIds)
                .filter(([, checked]) => checked)
                .map(([departmentId]) => ({
                  departmentId,
                  roleIds: [inviteDepartmentRoleIds[departmentId] || inviteRoleId].filter(Boolean),
                }))
            : [],
          departmentCompensations: Object.entries(selectedDepartmentIds)
            .filter(([, checked]) => checked)
            .map(([departmentId]) => ({
              departmentId,
              baseSalary: Number(inviteDepartmentBaseSalaries[departmentId] || 0),
            })),
        }),
      });
      if (!res.ok) {
        throw new Error(
          await resolveApiErrorMessage(res, t('backoffice.employees_invite_error')),
        );
      }
      setInviteSuccess(t('backoffice.employees_invite_success'));
      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setInviteRoleId('');
      setInviteCreateUserAccount(true);
      setSelectedDepartmentIds({});
      setInviteDepartmentRoleIds({});
      setInviteDepartmentBaseSalaries({});
      setShowInviteForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('backoffice.employees_invite_error'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const availableRoleNames = useMemo(() => (
    Array.from(
      new Set(
        employees.flatMap((emp) => {
          const assigned = (emp.roles || []).map((r) => r.name).filter(Boolean);
          return assigned.length > 0 ? assigned : [fallbackRoleLabel(emp.type)];
        }),
      ),
    ).sort((a, b) => a.localeCompare(b))
  ), [employees]);

  const availableDepartments = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; slug: string }>();
    employees.forEach((emp) => {
      (emp.departments || []).forEach((dep) => {
        if (!dep?.id) return;
        byId.set(dep.id, { id: dep.id, name: dep.name, slug: dep.slug });
      });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees.filter((emp) => {
      if (statusFilter !== 'all' && emp.status !== statusFilter) return false;
      if (roleFilter !== 'all') {
        const roleNames = (emp.roles || []).map((r) => r.name).filter(Boolean);
        const effectiveRoles = roleNames.length > 0 ? roleNames : [fallbackRoleLabel(emp.type)];
        const hasRole = effectiveRoles.some((name) => name === roleFilter);
        if (!hasRole) return false;
      }
      if (departmentFilter !== 'all') {
        const hasDepartment = (emp.departments || []).some((d) => d.id === departmentFilter);
        if (!hasDepartment) return false;
      }
      if (!q) return true;
      const name = String(emp.employeeProfile?.fullName || '').toLowerCase();
      const email = String(emp.email || '').toLowerCase();
      const roleNames = (emp.roles || []).map((r) => r.name.toLowerCase()).join(' ');
      const departmentNames = (emp.departments || []).map((d) => d.name.toLowerCase()).join(' ');
      return (
        name.includes(q)
        || email.includes(q)
        || roleNames.includes(q)
        || departmentNames.includes(q)
      );
    });
  }, [departmentFilter, employees, query, roleFilter, statusFilter]);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.employees_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.employees_subtitle')}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="mb-1 text-sm font-semibold text-slate-900">
                {t('backoffice.employees_invite_title')}
              </h2>
              <p className="text-xs text-slate-500">
                {t(
                  'admin.employees_invite_description',
                  'Invite a new team member to Lewouhmart.',
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowInviteForm((prev) => !prev);
                setError(null);
                setInviteSuccess(null);
              }}
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500"
            >
              {showInviteForm
                ? t('backoffice.employees_invite_hide_button', 'Hide form')
                : t('backoffice.employees_invite_show_button', 'Add employee')}
            </button>
          </div>

          {error && (
            <p className="mt-3 text-xs text-red-500">
              {error}
            </p>
          )}
          {inviteSuccess && (
            <p className="mt-3 text-xs text-emerald-600">
              {inviteSuccess}
            </p>
          )}

          {showInviteForm && (
            <form
              className="mt-4 grid gap-4 sm:grid-cols-2"
              onSubmit={handleInviteSubmit}
            >
              <div className="space-y-1.5 text-sm sm:col-span-2">
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={inviteCreateUserAccount}
                    onChange={(e) => setInviteCreateUserAccount(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  Create system login access now
                </label>
              </div>
              <div className="space-y-1.5 text-sm sm:col-span-2">
                <label className="block text-xs font-medium text-slate-700" htmlFor="inviteEmail">
                  {t('backoffice.employees_invite_email_label')}
                </label>
                <input
                  id="inviteEmail"
                  type="email"
                  required={inviteCreateUserAccount}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="inviteName">
                  {t('backoffice.employees_invite_name_label')}
                </label>
                <input
                  id="inviteName"
                  type="text"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="inviteRoleId">
                  {t('backoffice.employees_invite_type_label')}
                  {inviteCreateUserAccount ? <span className="ml-0.5 text-red-500">*</span> : null}
                </label>
                <select
                  id="inviteRoleId"
                  required={inviteCreateUserAccount}
                  disabled={!inviteCreateUserAccount}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  value={inviteRoleId}
                  onChange={(e) => setInviteRoleId(e.target.value)}
                >
                  <option value="" disabled>
                    {t('backoffice.roles_select_placeholder', 'Select a role')}
                  </option>
                  {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="space-y-1.5 text-sm">
                <PhoneInput
                  id="invitePhone"
                  label={t('backoffice.employees_invite_phone_label')}
                  value={invitePhone}
                  onChange={setInvitePhone}
                />
              </div>
              <div className="space-y-1.5 text-sm sm:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <label className="block text-xs font-medium text-slate-700">
                    Departments
                    <span className="ml-0.5 text-red-500">*</span>
                  </label>
                  <span className="text-[11px] font-medium text-slate-500">
                    {Object.values(selectedDepartmentIds).filter(Boolean).length} selected
                  </span>
                </div>
                {departments.length === 0 ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    No active departments found. Create a department first.
                  </p>
                ) : (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-2">
                    {departments.map((dep) => (
                      <label
                        key={dep.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-xs transition ${
                          selectedDepartmentIds[dep.id]
                            ? 'border-brand-300 bg-brand-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedDepartmentIds[dep.id]}
                          onChange={() => {
                            setSelectedDepartmentIds((prev) => ({
                              ...prev,
                              [dep.id]: !prev[dep.id],
                            }));
                            setInviteDepartmentRoleIds((prev) => ({
                              ...prev,
                              [dep.id]: prev[dep.id] || inviteRoleId || '',
                            }));
                            setInviteDepartmentBaseSalaries((prev) => ({
                              ...prev,
                              [dep.id]: prev[dep.id] || '',
                            }));
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        <div>
                          <div className="font-semibold text-slate-900">{dep.name}</div>
                          <div className="mt-0.5 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                            /{dep.slug}
                          </div>
                          {selectedDepartmentIds[dep.id] ? (
                            <div className="mt-2 space-y-2">
                              <select
                                value={inviteDepartmentRoleIds[dep.id] || inviteRoleId || ''}
                                onChange={(e) =>
                                  setInviteDepartmentRoleIds((prev) => ({
                                    ...prev,
                                    [dep.id]: e.target.value,
                                  }))}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-900"
                              >
                                <option value="" disabled>Select role for this department</option>
                                {roles.map((r) => (
                                  <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Department base salary"
                                value={inviteDepartmentBaseSalaries[dep.id] || ''}
                                onChange={(e) =>
                                  setInviteDepartmentBaseSalaries((prev) => ({
                                    ...prev,
                                    [dep.id]: e.target.value,
                                  }))}
                                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-900"
                              />
                            </div>
                          ) : null}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={
                    inviteSubmitting
                    || departments.length === 0
                    || Object.values(selectedDepartmentIds).every((v) => !v)
                    || (inviteCreateUserAccount
                      && Object.entries(selectedDepartmentIds)
                        .filter(([, checked]) => checked)
                        .some(([departmentId]) => !(inviteDepartmentRoleIds[departmentId] || inviteRoleId)))
                  }
                  className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inviteSubmitting
                    ? t('backoffice.employees_invite_sending', 'Sending…')
                    : t('backoffice.employees_invite_submit', 'Send invite')}
                </button>
              </div>
            </form>
          )}
        </section>

        <section className="workspace-panel">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900">
              {t('backoffice.employees_table_title', 'Team members')}
            </h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/workspace/payroll')}
                className="rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-700 hover:border-indigo-400 hover:bg-indigo-100"
              >
                Open payroll
              </button>
              <button
                type="button"
                onClick={() => navigate('/workspace/roles')}
                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
              >
                {t('backoffice.sidebar_roles', 'Roles')}
              </button>
            </div>
          </div>
          <div className="mb-4 grid gap-2 md:grid-cols-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('common.search', { defaultValue: 'Search' })}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/60"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/60"
            >
              <option value="all">All roles</option>
              {availableRoleNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/60"
            >
              <option value="all">All departments</option>
              {availableDepartments.map((dep) => (
                <option key={dep.id} value={dep.id}>{dep.name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/60"
            >
              <option value="all">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="BLOCKED">Blocked</option>
            </select>
          </div>

          {loading ? (
            <p className="text-sm text-slate-600">{t('backoffice.employees_table_loading', 'Loading employees…')}</p>
          ) : filteredEmployees.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.employees_table_empty', 'No employees yet. Send an invite to get started.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs sm:text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-4">{t('backoffice.employees_table_col_name', 'Name')}</th>
                    <th className="py-2 pr-4">Employee code</th>
                    <th className="py-2 pr-4">{t('backoffice.employees_table_col_email', 'Email')}</th>
                    <th className="py-2 pr-4">{t('backoffice.employees_table_col_status', 'Status')}</th>
                    <th className="py-2 pr-4 text-right">{t('backoffice.employees_table_col_actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id}>
                      <td className="py-2 pr-4">
                        <div className="font-medium text-slate-900">
                          {emp.employeeProfile?.fullName || '—'}
                        </div>
                        {emp.recordKind === 'PERSONNEL_SIMPLE' ? (
                          <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                            No login
                          </div>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {emp.employeeCode || '—'}
                      </td>
                      <td className="py-2 pr-4 text-slate-800">
                        {emp.email || '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-col gap-1">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-700">
                            {emp.status}
                          </span>
                          {emp.status === 'BLOCKED' &&
                          emp.blockedReason === 'INTRUSION_SELF_REPORT' ? (
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                              {t(
                                'backoffice.employees_blocked_intrusion',
                                'Blocked via self-service (intrusion)',
                              )}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/workspace/employees/${emp.id}`)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

    </MainLayout>
  );
}

export default EmployeesPage;

